import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Drawer,
  Form,
  Input,
  message,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import {
  CREATE_ERROR,
  EDIT_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { requiredRule } from 'utils/forms/validationRules';
import { GroupsService } from 'services/GroupsService';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';

function GroupsForm({ isActive, initialValues, users, onSuccess, onClose }) {
  const [form] = Form.useForm();
  const isEditing = initialValues !== null;
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    if (initialValues) {
      const { title, code, description, users } = initialValues;

      form.setFieldsValue({ title, code, description });
      setSelectedUsers(users);
    } else {
      form.resetFields();
      setSelectedUsers([]);
    }
  }, [initialValues]);

  useEffect(() => {
    form.setFieldsValue({
      users: selectedUsers.map((item) => item.u_id),
    });
  }, [selectedUsers]);

  const removeUser = (id) => {
    const copy = [...selectedUsers];
    const result = copy.filter((user) => user.u_id !== id);

    setSelectedUsers(result);
  };

  const setResponsible = (id, value) => {
    const idx = selectedUsers.findIndex((user) => user.u_id === id);
    const copy = [...selectedUsers];
    copy[idx].isResponsible = value;

    setSelectedUsers(copy);
  };

  const handleSubmit = (values) => {
    const payload = {
      ...values,
      users: selectedUsers,
    };

    if (isEditing) {
      handleUpdate(payload, initialValues.id);
    } else {
      handleCreate(payload);
    }
  };

  const handleCreate = async (payload) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await GroupsService.createGroups(payload);

      if (isCreated) {
        handleConfirm();
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (payload, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await GroupsService.updateGroups(payload, id);

      if (isUpdated) {
        handleConfirm();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const handleConfirm = () => {
    onSuccess();
    onClose();
    form.resetFields();
  };

  return (
    <Drawer
      title={isEditing ? 'Редактирование группы' : 'Создание группы'}
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        style={{ width: '100%' }}
        labelAlign="right"
      >
        <Form.Item
          name="title"
          label="Наименование группы"
          rules={[requiredRule]}
        >
          <Input placeholder="Введите наименование группы" allowClear />
        </Form.Item>

        <Form.Item name="description" label="Описание">
          <Input placeholder="Введите описание группы" allowClear />
        </Form.Item>

        <Form.Item name="code" label="Код">
          <Input placeholder="Введите код группы" allowClear />
        </Form.Item>

        <Form.Item name="users" label="Пользователи" rules={[requiredRule]}>
          <Select
            allowClear
            showArrow
            showSearch
            mode="multiple"
            maxTagCount="responsive"
            placeholder="Выберите пользователя"
            onChange={(_, options) =>
              setSelectedUsers(
                options.map((option) => ({
                  u_id: option.value,
                  full_name: option.children,
                  isResponsible: false,
                }))
              )
            }
          >
            {users?.map(({ id, full_name }) => (
              <Select.Option key={id} value={id}>
                {full_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {selectedUsers.length !== 0 && (
          <Col
            offset={CONTENT_SPAN}
            style={{
              marginBottom: '10px',
            }}
          >
            <Typography.Text>Добавление ответственного</Typography.Text>
          </Col>
        )}

        {selectedUsers?.map(({ u_id, full_name, isResponsible }) => {
          return (
            <Col offset={CONTENT_SPAN} key={u_id}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '5px',
                }}
              >
                <Typography.Text>{full_name}</Typography.Text>
                <Space>
                  <Switch
                    id={u_id}
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                    defaultChecked={isResponsible}
                    onChange={(value) => setResponsible(u_id, value)}
                  />
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={() => removeUser(u_id)}
                  />
                </Space>
              </div>
            </Col>
          );
        })}

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { GroupsForm };
